import PageSettings from 'client/components/core/Sidebar/components/Settings/PageSettings'
import { SIDEBAR_CONTAINER_ID } from 'client/constants/editorIds'
import CustomFontsProvider from 'client/context/CustomFontsContext'
import IconsProvider from 'client/context/IconsContext'
import {
  useSidebarVisibilityActionsContext,
  useSidebarVisibilityContext,
} from 'client/context/SidebarVisibilityContext'
import UserResourcesProvider from 'client/context/UserResourcesContext'
import useManagement from 'client/hooks/useManagement'
import usePresentPage, {
  selectors as pageSelectors,
} from 'client/hooks/usePresentPage'
import { selectors } from 'client/reducers/managementReducer'
import { ManagementState } from 'client/store'
import { ToggleSidebarIcon } from 'common/icons/toggle-sidebar-icon'
import { EntitiesStructure } from 'common/types/Page'
import React, { useEffect, useRef, memo } from 'react'
import { PagesManagement } from 'src/editor/layout/sidebar/pages-management'
import { Library } from './library'
import { PopupManagement } from './popup-management'
import { EntitySettings } from './settings/entity-settings'
import { SettingsOverlayContextProvider } from './settings/settings-overlay-context'
import { WebsiteStylesSettings } from './website-styles-settings'
import { WebsiteSettings } from './website-settings'
import { twMerge } from 'tailwind-merge'

const combinedSidebarSelector = (state: ManagementState) => ({
  editingId: selectors.getEditingId(state),
  isPopupActive: selectors.isPopupActive(state),
  isPopupManagement: selectors.isPopupManagement(state),
  isWebsiteStylesSettings: selectors.isWebsiteStylesSettings(state),
  isPagesManagementActive: selectors.isPagesManagementActive(state),
  isPageEditing: selectors.isPageEditing(state),
  isWebsiteSettings: selectors.isWebsiteSettings(state),
})

export const Sidebar = memo(() => {
  const sidebarContainerRef = useRef<HTMLDivElement>(null)
  const sidebarState = useManagement(combinedSidebarSelector)
  const {
    editingId,
    isPopupActive,
    isPopupManagement,
    isWebsiteStylesSettings,
    isPagesManagementActive,
    isPageEditing,
    isWebsiteSettings,
  } = sidebarState
  const entity = usePresentPage((p: { entities: EntitiesStructure }) =>
    pageSelectors.getEntityById(p, editingId),
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll reset on entity switch
  useEffect(() => {
    if (sidebarContainerRef.current) {
      sidebarContainerRef.current.scrollTop = 0
    }
  }, [editingId])

  const getSidebar = () => {
    if (entity) {
      return (
        <SettingsOverlayContextProvider>
          <EntitySettings entity={entity} />
        </SettingsOverlayContextProvider>
      )
    }

    if (isPageEditing) {
      return (
        <SettingsOverlayContextProvider>
          <PageSettings />
        </SettingsOverlayContextProvider>
      )
    }
    if (isWebsiteSettings) {
      return <WebsiteSettings />
    }

    if (isPopupManagement && !isPopupActive) {
      return <PopupManagement />
    }

    if (isWebsiteStylesSettings) {
      return <WebsiteStylesSettings />
    }

    if (isPagesManagementActive) {
      return <PagesManagement />
    }

    return <Library />
  }
  const isVisible = useSidebarVisibilityContext()
  return (
    <aside className="relative h-[calc(100vh-var(--layout-header-height))] bg-bgPrimaryBase">
      <div
        className={twMerge(
          'h-[calc(100%-10px)] overflow-y-auto border-border200 border-r',
          isVisible && 'p-[var(--sidebar-padding)]',
        )}
        ref={sidebarContainerRef}
        id={SIDEBAR_CONTAINER_ID}
      >
        <ToggleSidebarButton />
        <IconsProvider>
          <UserResourcesProvider>
            <CustomFontsProvider>{getSidebar()}</CustomFontsProvider>
          </UserResourcesProvider>
        </IconsProvider>
      </div>
    </aside>
  )
})

const ToggleSidebarButton = () => {
  const isVisible = useSidebarVisibilityContext()
  const { toggleSidebar } = useSidebarVisibilityActionsContext()

  return (
    <button
      onClick={toggleSidebar}
      type="button"
      className="absolute top-[calc(50%-var(--layout-header-height))] left-full z-50 flex h-14 w-[18px] cursor-pointer items-center justify-center text-bgTertiary hover:text-gray-150"
    >
      <span className="-z-10 absolute top-0 left-0 transition-colors">
        <ToggleSidebarIcon />
      </span>
      {isVisible ? (
        <i className="fa-regular fa-chevron-left text-iconSecondary" />
      ) : (
        <i className="fa-regular fa-chevron-right text-iconSecondary" />
      )}
    </button>
  )
}
